// Étendre globalThis avec notre propriété personnalisée
declare global {
  // 'var' est nécessaire pour les déclarations globales avec 'declare global'
  // eslint-disable-next-line no-var
  var __ApiControllersMap: Map<string, AbortController> | undefined;
  // eslint-disable-next-line no-var
  var __ApiLoadingStates: Map<string, boolean> | undefined;
}

export const useCoreApiHelper = () => {
  const controllersMap = globalThis.__ApiControllersMap || new Map<string, AbortController>();
  globalThis.__ApiControllersMap = controllersMap;

  // Map pour suivre l'état de loading de chaque requête par ID
  const loadingStates = globalThis.__ApiLoadingStates || new Map<string, boolean>();
  globalThis.__ApiLoadingStates = loadingStates;

  // Récupère l'état de loading pour un ID de requête spécifique
  const getLoadingState = (requestId: string) => {
    return loadingStates.get(requestId) || false;
  };

  // Fonction pour vérifier si une requête avec cet ID est en cours
  const isLoading = (requestId: string) => {
    return getLoadingState(requestId);
  };

  const cancelRequestIfDoubled = async <T extends { data?: any; error?: any }>(options: {
    requestId: string;
    execute: (signal: AbortSignal) => Promise<T>;
    onLoadingStart?: () => void; // Callback quand le loading commence
    onLoadingEnd?: () => void; // Callback quand le loading se termine
    onError?: (error: ApiError) => void; // Callback quand une erreur survient
    onSuccess?: (data: NonNullable<T['data']>) => void; // Callback quand la requête est réussie
    onAbort?: () => void; // Callback quand la requête est annulée
  }) => {
    // Annuler la requête précédente si elle existe
    const previous = controllersMap.get(options.requestId);

    if (previous) {
      previous.abort();
      controllersMap.delete(options.requestId);
      if (options.onAbort) {
        options.onAbort();
      }
      // Ne pas changer l'état de loading ici, car une nouvelle requête va démarrer
    }

    // Créer le nouveau controller pour cette requête
    const controller = new AbortController();
    controllersMap.set(options.requestId, controller);

    // Définir l'état de loading à true pour ce requestId
    loadingStates.set(options.requestId, true);
    if (options.onLoadingStart) {
      options.onLoadingStart();
    }

    try {
      const data = await options.execute(controller.signal);
      if (options.onSuccess && data.data) {
        options.onSuccess(data.data);
      } else {
        options.onError?.(data.error);
      }
      return data;
    } finally {
      // Ne mettre le loading à false que si c'est le dernier controller pour cet ID
      if (controllersMap.get(options.requestId) === controller) {
        controllersMap.delete(options.requestId);
        loadingStates.set(options.requestId, false);

        if (options.onLoadingEnd) {
          options.onLoadingEnd();
        }
      }
    }
  };

  return {
    cancelRequestIfDoubled,
    isLoading,
    getLoadingState,
  };
};
